"use client";

import * as React from "react";
import { Link } from "../../../primitives";
import { cn } from "@marketing-sites/utils/cn";
import { useLocalizeHref } from "@marketing-sites/l10n/providers/localize-href-context";
import { useHeaderContext } from "../context";
import { ExternalLinkIcon } from "../icons/external-link-icon";
import { isSameDomain } from "../lib/is-same-domain";

type NavigationLinkProps = {
  href: string;
  /** Text used for analytics tracking (and as fallback content) */
  text?: string;
  /** Override the text sent in the navItemClick analytics event (defaults to text) */
  analyticsText?: string;
  /** Stable slug for the navItemClick event, so the identifier never keys on
   *  rendered copy. */
  analyticsId: string;
  location: string;
  external?: boolean;
  /** When true, the external arrow icon is always visible instead of only on hover */
  externalIconVisible?: boolean;
  /**
   * Skip the built-in external arrow icon and its `inline-flex items-center`
   * wrapper layout. For callers that render their own affordance and control
   * their own layout (e.g. the floating nav's tiered links).
   */
  suppressExternalIcon?: boolean;
  className?: string;
  /** Optional children - if not provided, text will be rendered */
  children?: React.ReactNode;
  ariaLabel?: string;
} & Omit<
  React.AnchorHTMLAttributes<HTMLAnchorElement>,
  "href" | "children" | "className"
>;

export const NavigationLink = React.forwardRef<
  HTMLAnchorElement,
  NavigationLinkProps
>(
  (
    {
      href,
      text,
      analyticsText,
      analyticsId,
      location,
      external = false,
      externalIconVisible = false,
      suppressExternalIcon = false,
      className,
      children,
      ariaLabel,
      ...rest
    },
    ref,
  ) => {
    const { LinkComponent } = useHeaderContext();
    const { onClick: restOnClick, ...restWithoutClick } = rest;
    const localizeHref = useLocalizeHref();
    const localizedHref = localizeHref(href);

    const dispatchAnalyticsEvent = React.useCallback(
      (destinationOverride?: string) => {
        if (typeof window !== "undefined") {
          window.dispatchEvent(
            new CustomEvent("navItemClick", {
              detail: {
                text: analyticsText ?? text ?? "",
                destination: destinationOverride ?? href,
                location,
                analyticsId,
                ...(analyticsText ? { analyticsText } : {}),
              },
            }),
          );
        }
      },
      [analyticsText, text, href, location, analyticsId],
    );

    const handleClick: React.MouseEventHandler<HTMLAnchorElement> =
      React.useCallback(
        (e) => {
          // Read the DOM href so any decoration (e.g. gtm_id from
          // AddUrlParamsToCrossSiteLinks) applied during the capture phase is
          // reflected in the analytics event.
          dispatchAnalyticsEvent(e.currentTarget.href);
          restOnClick?.(e);
        },
        [dispatchAnalyticsEvent, restOnClick],
      );

    const content = children ?? text;
    const isInternal = isSameDomain(href);

    // External links (explicit or cross-domain) use anchor tags
    if (external) {
      return (
        <a
          {...restWithoutClick}
          ref={ref}
          href={localizedHref}
          target="_blank"
          rel="noopener noreferrer"
          className={cn(
            "group",
            !suppressExternalIcon && "inline-flex items-center",
            className,
          )}
          onClick={handleClick}
          aria-label={ariaLabel}
        >
          {content}
          {!suppressExternalIcon && (
            <ExternalLinkIcon
              className={cn(
                "h-3 w-3 shrink-0 opacity-60",
                externalIconVisible
                  ? "block"
                  : "opacity-0 group-hover:opacity-60",
              )}
            />
          )}
        </a>
      );
    }

    // Cross-domain links use anchor tags
    if (!isInternal) {
      return (
        <a
          {...restWithoutClick}
          ref={ref}
          href={localizedHref}
          className={className}
          onClick={handleClick}
          aria-label={ariaLabel}
        >
          {content}
        </a>
      );
    }

    // Same-domain links: if the consumer injected a LinkComponent, use it (e.g.
    // a native anchor for routes whose href is mutated at click time). Otherwise
    // default to the monorepo Link (wraps next/link); we dispatch our own
    // navItemClick event, so disable its analytics to avoid double-firing.
    if (LinkComponent) {
      return (
        <LinkComponent
          href={href}
          className={className}
          onClick={() => dispatchAnalyticsEvent(localizedHref)}
          aria-label={ariaLabel}
        >
          {content}
        </LinkComponent>
      );
    }

    return (
      <Link
        href={href}
        className={className}
        onClick={() => dispatchAnalyticsEvent(localizedHref)}
        aria-label={ariaLabel}
        disableAnalytics
      >
        {content}
      </Link>
    );
  },
);

NavigationLink.displayName = "NavigationLink";
